import {
  BasicInstance,
  ConnectorInstance,
  Datapoint,
  DefaultDatapointTypes,
  WindowMessageDatapoint,
  WindowMessageEnvelope
} from '@olive/oli-types';
import ConnectivityHolder from '../ConnectivityHolder';
import ConnectivityManager from '../ConnectivityManager';
import AbstractDispatcher from './AbstractDispatcher';
import * as JsonataModule from 'jsonata';
const jsonata = JsonataModule.default;

const REGISTER_ORIGIN_TOPIC = 'oliveRegisterOrigin'
const ACKKNOWLEDGE_ORIGIN_TOPIC = 'oliveAcknowledgeOrigin'

export default class WindowDispatcher extends AbstractDispatcher {
  connectivityManager: ConnectivityManager;
  origins: {
    [origin: string]: {
      origin: string,
      win: MessageEventSource
    }
  }

  constructor(
    connectivityHolder: ConnectivityHolder,
    connectivityManager: ConnectivityManager
  ) {
    super(connectivityHolder);
    this.connectivityManager = connectivityManager;
    this.origins = {};
    window.addEventListener('message', this.#onMessage.bind(this));
  }

  async #onMessage(message: MessageEvent) {
    console.log('#onMessage', message);
    const envelope = message.data as WindowMessageEnvelope;

    if (this.#isRegisterOriginMessage(envelope)) {
      this.#registerOriginMessage(message);
      return;
    }

    console.log('event to dispatch', envelope);

    const allConnectivitys = this.connectivityHolder.getAll();
    if (!allConnectivitys) {
      return;
    }
    const connectors = Object.keys(allConnectivitys).map(key => allConnectivitys[key]).filter(connectivity => connectivity.type === 'connectorInstance');


    const matchingConnectors = connectors.filter(connector => {
      const connectorInstance = connector as ConnectorInstance;
      const datapointID = envelope.datapointID && connectorInstance.elements?.[0] && (connectorInstance.elements?.[0] as BasicInstance)?.instanceID === envelope.datapointID;
      if(datapointID)
      {
        return true;
      }
      const firstElement = connectorInstance.elements?.[0] as BasicInstance;
      if(firstElement?.instanceID)
      {
        const firstElementDatapoint = this.connectivityHolder.get(firstElement.instanceID);
        if(firstElementDatapoint?.type === 'datapoint' && firstElementDatapoint.datapoint.type === 'windowmessage') 
        {
          return (firstElementDatapoint as WindowMessageDatapoint).datapoint.windowmessage.topic === envelope.topic;
        }
      }

      return false;
    });
    console.log('matchingConnectors', matchingConnectors);

    const dispatchers = matchingConnectors.map(connector => this.connectivityManager.invoke({
      id: connector.instanceID || connector.instanceOf,
      input: envelope.payload
    }));
    const result = await Promise.all(dispatchers);
    console.log('dispatched: ', result);
  }

  #isRegisterOriginMessage(envelope: WindowMessageEnvelope)
  {
    return envelope.topic === REGISTER_ORIGIN_TOPIC;
  }

  #registerOriginMessage(message: MessageEvent) {
    console.info('registering new window origin');
    const envelope = message.data as WindowMessageEnvelope;
    const payload = envelope.payload as unknown as {id: string, origin: string};
    if(payload.origin && payload.id)
    {
      this.origins[payload.id] = {
        origin: payload.origin,
        win: message.source
      }
      this.#acknowldegeOriginMessage(payload.id);
    }
    else {
      console.error('Missing origin or window in register origin message: ', payload);
    }
  }

  #acknowldegeOriginMessage(originID: string)
  {
    this.origins[originID].win.postMessage({
      topic: ACKKNOWLEDGE_ORIGIN_TOPIC
    }, this.origins[originID].origin as WindowPostMessageOptions);
  }

  #getDatapoint(id: string | undefined | null): Datapoint | null {
    if (!id) {
      return null;
    }
    return this.connectivityHolder.get(id) as Datapoint;
  }

  canDispatch(props: { id: string, datapoint: Datapoint | undefined }): boolean {
    const datapoint = !props.datapoint ? this.#getDatapoint(props.id) : props.datapoint;
    if (!datapoint) {
      return false;
    }
    const type = datapoint.datapoint.type;
    return type === DefaultDatapointTypes.WINDOW_MESSAGE;
  }

  async dispatch(props: { id: string; input?: object }) {

    const datapoint = this.#getDatapoint(props.id) as WindowMessageDatapoint;
    if (!datapoint) {
      console.warn('Datapoint not found: ' + props.id);
      return null;
    }
    const windowMessage = datapoint.datapoint.windowmessage;
    const parameters = windowMessage.payload;

    const resultMap = {
      input: props.input
    };
    let resolvedInstanceParameters;
    if (parameters) {
      resolvedInstanceParameters = await this.resolveInstanceParameters(parameters, resultMap);
    }
    const enrichedInput = {
      ...resolvedInstanceParameters,
      ...props.input
    };


    const origin = windowMessage.origin;
    const originEntry = this.origins[origin];
    if (!originEntry) {
      console.error('Tried to dispatch message to unknown origin: ' + origin);
    }

    const topic = windowMessage.topic;

    const envelope = {
      topic,
      payload: enrichedInput
    };

    //@ts-ignore
    originEntry.win.postMessage(envelope, originEntry.origin);



    return props.input;
  }

  // TODO MHA temporary copy-paste from ClientDispatcher
  private async resolveInstanceParameters(
    params: any,
    resultMap: { [key: string]: object }
  ): Promise<any> {
    const newParams = Array.isArray(params) ? [...params] : { ...params };

    for (const key in newParams) {
      const value = newParams[key];

      // 1) If it's a string that *might* contain ${...}:
      if (typeof value === 'string') {
        // Attempt to extract a JSONata expression
        const extracted = extractJsonataExpression(value);
        if (extracted !== null) {
          try {
            // Evaluate the JSONata expression against resultMap
            const result = await jsonata(extracted).evaluate(resultMap);
            // Store the evaluated result back
            newParams[key] = result;
          } catch (error) {
            console.error(`Error evaluating JSONata expression: ${value}`, error);
          }
        }
      }
      // 2) If it's an object or an array, recurse
      else if (typeof value === 'object' && value !== null) {
        newParams[key] = await this.resolveInstanceParameters(value, resultMap);
      }
    }

    return newParams;
  }

}

  // TODO MHA temporary copy-paste from ClientDispatcher
function extractJsonataExpression(str: string): string | null {
  const startIndex = str.indexOf('${');
  if (startIndex === -1) {
    return null; // No expression
  }

  let braceLevel = 0;
  let i = startIndex;

  // Scan until we find the matching '}'
  while (i < str.length) {
    if (str[i] === '{') {
      braceLevel++;
    } else if (str[i] === '}') {
      braceLevel--;
      // If we've closed all '{', we're at the matching '}'
      if (braceLevel === 0) {
        // Return everything *between* "${" and this "}"
        return str.substring(startIndex + 2, i);
      }
    }
    i++;
  }
  return null;
}